import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, IChartApi, ISeriesApi, CandlestickData, PriceScaleMode, UTCTimestamp, IPriceLine, MouseEventParams, ISeriesPrimitive, ISeriesPrimitivePaneRenderer, ISeriesPrimitivePaneView } from 'lightweight-charts';
import { Maximize2, Minimize2, Ruler, Tag, Target, CandlestickChart, LineChart, Activity, Settings, Sun, Moon, List, ChevronsDown, ChevronsUp, Droplet, Circle, Loader2 } from 'lucide-react';
import { Candle, Transaction } from '../types';

interface TradingChartProps {
  data: Candle[];
  isLoading?: boolean;
  transactions: Transaction[];
  symbol: string;
  interval: string;
  chartType: 'candlestick' | 'line';
  selectedTransactionId?: string | null;
  theme: 'dark' | 'light';
  breakEvenPrice?: number;
  isMeasureMode: boolean;
  onMeasureModeChange: (val: boolean) => void;
  showLabels?: boolean;
  onToggleLabels?: () => void;
  showBreakEven?: boolean;
  onToggleBreakEven?: () => void;
  showEMA?: boolean;
  onToggleEMA?: () => void;
  emaPeriod?: number;
  onEmaPeriodChange?: (period: number) => void;
  useDynamicOpacity?: boolean;
  onToggleDynamicOpacity?: () => void;
  useUniformBubbleSize?: boolean;
  onToggleUniformBubbleSize?: () => void;
  onIntervalChange?: (interval: string) => void;
  onChartTypeChange?: (type: 'candlestick' | 'line') => void;
  currentPrice?: number;
  isFullscreen?: boolean;
  onToggleFullscreen?: (val: boolean) => void;
  isExpandedDown?: boolean;
  onToggleExpandedDown?: () => void;
  onToggleTheme?: () => void;
  onOpenTransactions?: () => void;
  symbolList?: any[];
  onSymbolChange?: (symbol: string) => void;
  onDeleteTransaction?: (id: string) => void; 
}

interface MarkerTooltip {
  visible: boolean;
  x: number;
  y: number;
  transaction: Transaction | null;
}

interface MeasurePoint {
  price: number;
  time: number;
  logical: number | null;
  x: number;
  y: number;
}

// --- BUBBLE PRIMITIVE IMPLEMENTATION ---

interface BubbleData {
  time: UTCTimestamp;
  price: number;
  radius: number;
  color: string;
  id: string;
  text?: string;
  textColor?: string;
  isSelected?: boolean;
}

class BubblePaneRenderer implements ISeriesPrimitivePaneRenderer {
  constructor(private _data: { x: number; y: number; radius: number; color: string; text?: string; textColor?: string; isSelected?: boolean }[]) {}

  draw(target: any) {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context;
      ctx.save();
      this._data.forEach(d => {
        if (d.x < -50 || d.x > scope.mediaSize.width + 50) return;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.radius, 0, 2 * Math.PI);
        ctx.fillStyle = d.color;
        ctx.fill();
        if (d.isSelected) {
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#fff'; 
          ctx.stroke();
        }
      });
      ctx.font = "bold 10px 'Glegoo', serif";
      ctx.textAlign = "center";
      this._data.forEach(d => {
        if (d.x < -50 || d.x > scope.mediaSize.width + 50) return;
        if (d.text) {
          ctx.fillStyle = d.textColor || '#000';
          ctx.fillText(d.text, d.x, d.y - d.radius - 5);
        }
      });
      ctx.restore();
    });
  }
}

class BubblePaneView implements ISeriesPrimitivePaneView {
  constructor(private _source: BubblePrimitive) {}
  renderer() {
    const chart = this._source.chart;
    const series = this._source.series;
    if (!chart || !series) return null;
    const timeScale = chart.timeScale();
    const bubbles: any[] = [];
    this._source.data.forEach(b => {
      const x = timeScale.timeToCoordinate(b.time);
      const y = series.priceToCoordinate(b.price);
      if (x !== null && y !== null) {
        bubbles.push({ x, y, radius: b.radius, color: b.color, text: b.text, textColor: b.textColor, isSelected: b.isSelected });
      }
    });
    return new BubblePaneRenderer(bubbles);
  }
  zOrder(): "top" | "bottom" | "normal" { return 'top'; }
}

class BubblePrimitive implements ISeriesPrimitive {
  public chart: IChartApi | null = null;
  public series: ISeriesApi<any> | null = null;
  public data: BubbleData[] = [];
  private _paneView: BubblePaneView;
  private _requestUpdate?: () => void;
  constructor() { this._paneView = new BubblePaneView(this); }
  attached({ chart, series, requestUpdate }: { chart: IChartApi; series: ISeriesApi<any>; requestUpdate: () => void }) {
    this.chart = chart; this.series = series; this._requestUpdate = requestUpdate;
  }
  detached() { this.chart = null; this.series = null; this._requestUpdate = undefined; }
  paneViews() { return [this._paneView]; }
  setData(data: BubbleData[]) { this.data = data; if (this._requestUpdate) this._requestUpdate(); }
}

const toBoldDigits = (text: string | number): string => {
  const str = String(text);
  const boldDigits = ['𝟎', '𝟏', '𝟐', '𝟑', '𝟒', '𝟓', '𝟔', '𝟕', '𝟖', '𝟗'];
  return str.replace(/[0-9]/g, (digit) => boldDigits[parseInt(digit)] || digit);
};

const calculateEMA = (data: Candle[], count: number) => {
  if (data.length < count) return [];
  const k = 2 / (count + 1);
  const emaData = [];
  let sum = 0;
  for (let i = 0; i < count; i++) sum += data[i].close;
  let ema = sum / count;
  if (data[count - 1]?.time) emaData.push({ time: data[count - 1].time as UTCTimestamp, value: ema });
  for (let i = count; i < data.length; i++) {
    const price = data[i].close;
    ema = (price * k) + (ema * (1 - k));
    if (data[i]?.time) emaData.push({ time: data[i].time as UTCTimestamp, value: ema });
  }
  return emaData;
};

const TradingChart: React.FC<TradingChartProps> = ({ 
  data, isLoading, transactions, symbol, interval, chartType, selectedTransactionId, theme, 
  isMeasureMode, onMeasureModeChange, showLabels, onToggleLabels,
  showBreakEven, onToggleBreakEven, breakEvenPrice,
  showEMA, onToggleEMA, emaPeriod = 55,
  useDynamicOpacity, onToggleDynamicOpacity,
  useUniformBubbleSize, onToggleUniformBubbleSize,
  onIntervalChange, onChartTypeChange,
  isFullscreen: isFullscreenProp, onToggleFullscreen,
  isExpandedDown, onToggleExpandedDown,
  onToggleTheme, onOpenTransactions, currentPrice
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const mainSeriesRef = useRef<ISeriesApi<'Candlestick' | 'Line'> | null>(null);
  const emaSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const breakEvenLineRef = useRef<IPriceLine | null>(null);
  const measureStartLineRef = useRef<IPriceLine | null>(null);
  const measureEndLineRef = useRef<IPriceLine | null>(null);
  const lastCandleRef = useRef<Candle | null>(null);
  const bubblePrimitiveRef = useRef<BubblePrimitive | null>(null);
  const processedTransactionsRef = useRef<Transaction[]>([]);
  
  // Refs para persistencia de vista: Eliminamos el reset forzado por símbolo
  const hasEverLoadedData = useRef<boolean>(false);

  const [markerTooltip, setMarkerTooltip] = useState<MarkerTooltip>({ visible: false, x: 0, y: 0, transaction: null });
  const isFullscreen = isFullscreenProp !== undefined ? isFullscreenProp : false;
  const [measureStart, setMeasureStart] = useState<MeasurePoint | null>(null);
  const [measureCurrent, setMeasureCurrent] = useState<MeasurePoint | null>(null);
  const isMeasureModeRef = useRef(isMeasureMode);
  const measureStartRef = useRef(measureStart);

  useEffect(() => {
    isMeasureModeRef.current = isMeasureMode;
    measureStartRef.current = measureStart;
  }, [isMeasureMode, measureStart]);

  const timeframes = [
    { label: '1H', value: '1h' }, 
    { label: '4H', value: '4h' },
    { label: '1D', value: '1d' }, 
    { label: '1S', value: '1w' }, 
    { label: '1M', value: '1M' },
  ];

  const formatDuration = (start: number, end: number) => `${Math.floor(Math.abs(end - start) / 86400)}d`;
  const formatPrice = (val: number) => {
    const absVal = Math.abs(val);
    if (absVal < 1) return absVal.toLocaleString('es-ES', { minimumFractionDigits: 4 });
    if (absVal < 10) return absVal.toLocaleString('es-ES', { minimumFractionDigits: 3 });
    return absVal.toLocaleString('es-ES', { minimumFractionDigits: 2 });
  };

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const chart = createChart(chartContainerRef.current, {
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: theme === 'dark' ? '#d1d4dc' : '#1e293b', fontSize: 12, fontFamily: "'Glegoo', serif", },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      rightPriceScale: { mode: PriceScaleMode.Logarithmic, borderColor: theme === 'dark' ? '#262626' : '#f1f5f9', visible: true, autoScale: true },
      timeScale: { borderColor: theme === 'dark' ? '#262626' : '#f1f5f9', visible: true, timeVisible: true, rightOffset: 25, barSpacing: 12, fixLeftEdge: false },
      crosshair: { mode: 0, vertLine: { color: theme === 'dark' ? '#475569' : '#94a3b8', width: 1, style: 3, labelVisible: true }, horzLine: { color: theme === 'dark' ? '#475569' : '#94a3b8', width: 1, style: 3, labelVisible: true } },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });
    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0] && chartRef.current) chartRef.current.applyOptions({ width: entries[0].contentRect.width, height: entries[0].contentRect.height });
    });
    resizeObserver.observe(chartContainerRef.current);
    return () => { resizeObserver.disconnect(); if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; } };
  }, []);

  useEffect(() => {
    if (chartRef.current) chartRef.current.applyOptions({
      layout: { textColor: theme === 'dark' ? '#d1d4dc' : '#1e293b' },
      rightPriceScale: { borderColor: theme === 'dark' ? '#262626' : '#f1f5f9' },
      timeScale: { borderColor: theme === 'dark' ? '#262626' : '#f1f5f9' }
    });
  }, [theme]);

  // EFECTO DE CARGA DE DATOS: Persistencia total de rango (Símbolos y Temporalidades)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    
    // Capturamos el rango visible ANTES de cambiar los datos
    const timeScale = chart.timeScale();
    const currentVisibleRange = timeScale.getVisibleRange();

    if (mainSeriesRef.current) { chart.removeSeries(mainSeriesRef.current); mainSeriesRef.current = null; bubblePrimitiveRef.current = null; }
    if (emaSeriesRef.current) { try { chart.removeSeries(emaSeriesRef.current); } catch(e){} emaSeriesRef.current = null; }
    
    if (data.length === 0) { lastCandleRef.current = null; return; }
    
    lastCandleRef.current = { ...data[data.length - 1] };
    const latest = data[data.length - 1];
    let precision = 2; let minMove = 0.01;
    if (latest.close < 1) { precision = 4; minMove = 0.0001; } else if (latest.close < 10) { precision = 3; minMove = 0.001; }
    
    let newSeries: ISeriesApi<'Candlestick' | 'Line'>;
    
    if (chartType === 'candlestick') {
      newSeries = (chart as any).addCandlestickSeries({ priceFormat: { type: 'price', precision, minMove }, upColor: '#089981', downColor: '#f23645', borderVisible: false, wickUpColor: '#089981', wickDownColor: '#f23645' });
      newSeries.setData(data as CandlestickData[]);
    } else {
      newSeries = (chart as any).addLineSeries({ priceFormat: { type: 'price', precision, minMove }, color: '#2962ff', lineWidth: 2 });
      newSeries.setData(data.map(d => ({ time: d.time as UTCTimestamp, value: d.close })));
    }
    
    mainSeriesRef.current = newSeries;

    const primitive = new BubblePrimitive();
    (newSeries as any).attachPrimitive(primitive);
    bubblePrimitiveRef.current = primitive;

    // LÓGICA DE PERSISTENCIA:
    if (!hasEverLoadedData.current) {
      // Primera vez absoluta que se cargan datos: encajar contenido
      timeScale.fitContent();
      hasEverLoadedData.current = true;
    } else if (currentVisibleRange) {
      // Si ya existía una vista del usuario, mantenerla independientemente del símbolo o temporalidad
      timeScale.setVisibleRange(currentVisibleRange);
    }
    
  }, [data, chartType, theme, symbol]);

  useEffect(() => {
    const series = mainSeriesRef.current;
    if (!series || !currentPrice || !lastCandleRef.current) return;
    const prev = lastCandleRef.current;
    const updated: Candle = { ...prev, close: currentPrice, high: Math.max(prev.high, currentPrice), low: Math.min(prev.low, currentPrice) };
    if (chartType === 'candlestick') (series as ISeriesApi<'Candlestick'>).update(updated as CandlestickData);
    else (series as ISeriesApi<'Line'>).update({ time: updated.time as UTCTimestamp, value: updated.close });
    lastCandleRef.current = updated;
  }, [currentPrice, chartType]);

  useEffect(() => {
    if (!bubblePrimitiveRef.current || data.length === 0) return;
    const cleanSym = symbol.split(/[-/]/)[0].toUpperCase().replace(/[^A-Z0-9]/g, '');
    const filtered = transactions.filter(t => t.ticket.split(/[-/]/)[0].toUpperCase().replace(/[^A-Z0-9]/g, '') === cleanSym);
    processedTransactionsRef.current = [...filtered].sort((a, b) => a.timestamp - b.timestamp);
    
    const totals = filtered.map(x => x.total); const minTotal = Math.min(...totals); const maxTotal = Math.max(...totals); const rangeTotal = maxTotal - minTotal;
    const buys = filtered.filter(t => t.status.toLowerCase().includes('compra') || t.status.toLowerCase().includes('buy'));
    const sells = filtered.filter(t => t.status.toLowerCase().includes('venta') || t.status.toLowerCase().includes('sell'));
    const getStats = (txs: Transaction[]) => { if (txs.length === 0) return { min: 0, range: 0 }; const v = txs.map(t => t.total); return { min: Math.min(...v), range: Math.max(...v) - Math.min(...v) }; };
    const bStats = getStats(buys); const sStats = getStats(sells);
    const avTimes = data.map(d => d.time as number);

    const bubbles: BubbleData[] = filtered.map(t => {
      const isBuy = t.status.toLowerCase().includes('compra') || t.status.toLowerCase().includes('buy');
      const isSelected = selectedTransactionId != null && String(t.id).trim() === String(selectedTransactionId).trim();
      let dSize = useUniformBubbleSize ? 18 : (rangeTotal > 0 ? 12 + ((t.total - minTotal) / rangeTotal) * 22 : 23);
      let dAlpha = 1.0;
      if (useDynamicOpacity) {
        const s = isBuy ? bStats : sStats;
        dAlpha = s.range > 0 ? 0.25 + ((t.total - s.min) / s.range) * 0.7 : 0.8;
      }
      if (isSelected) dAlpha = 1.0;
      const base = isBuy ? `8, 153, 129` : `242, 54, 69`;
      const color = isSelected ? (theme === 'dark' ? 'rgba(56, 189, 248, 0.9)' : 'rgba(0, 0, 128, 0.9)') : `rgba(${base}, ${dAlpha.toFixed(2)})`;
      const renderTime = avTimes.length > 0 ? avTimes.reduce((p, c) => (Math.abs(c - t.timestamp) < Math.abs(p - t.timestamp) ? c : p)) : t.timestamp;
      return { time: renderTime as UTCTimestamp, price: t.price, radius: isSelected ? dSize / 1.5 : dSize / 2, color, id: t.id, text: (showLabels || isSelected) ? toBoldDigits(Math.round(t.total)) : undefined, textColor: theme === 'dark' ? '#ffffff' : '#000000', isSelected };
    });
    bubblePrimitiveRef.current.setData(bubbles);
  }, [transactions, selectedTransactionId, showLabels, theme, symbol, useDynamicOpacity, useUniformBubbleSize, data, chartType]);

  useEffect(() => {
    const series = mainSeriesRef.current;
    if (!series) return;
    if (breakEvenLineRef.current) { try { series.removePriceLine(breakEvenLineRef.current); } catch (e) {} breakEvenLineRef.current = null; }
    if (showBreakEven && breakEvenPrice && breakEvenPrice > 0) {
      try {
          breakEvenLineRef.current = series.createPriceLine({ price: breakEvenPrice, color: theme === 'dark' ? '#fbbf24' : '#d97706', lineWidth: 2, lineStyle: 2, axisLabelVisible: true, title: 'PROMEDIO' });
      } catch (e) {}
    }
  }, [showBreakEven, breakEvenPrice, theme, mainSeriesRef.current]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || data.length === 0) return;
    
    if (emaSeriesRef.current) { 
      try { chart.removeSeries(emaSeriesRef.current); } catch (e) {} 
      emaSeriesRef.current = null; 
    }
    
    if (showEMA) {
      try {
          const emaData = calculateEMA(data, emaPeriod);
          if (emaData.length > 0) {
              emaSeriesRef.current = (chart as any).addLineSeries({ 
                color: theme === 'dark' ? '#f59e0b' : '#172554', 
                lineWidth: 1, 
                priceFormat: { type: 'price' }, 
                crosshairMarkerVisible: false, 
                lastValueVisible: false, 
                priceLineVisible: false, 
                title: `EMA ${emaPeriod}` 
              });
              emaSeriesRef.current?.setData(emaData);
          }
      } catch (e) {
        console.error("Error drawing EMA", e);
      }
    }
  }, [showEMA, emaPeriod, theme, data, chartType]); 

  useEffect(() => {
    const series = mainSeriesRef.current;
    if (!series) return;
    if (measureStartLineRef.current) { try { series.removePriceLine(measureStartLineRef.current); } catch (e) {} measureStartLineRef.current = null; }
    if (measureEndLineRef.current) { try { series.removePriceLine(measureEndLineRef.current); } catch (e) {} measureEndLineRef.current = null; }
    if (isMeasureMode) {
      if (measureStart) measureStartLineRef.current = series.createPriceLine({ price: measureStart.price, color: '#2962ff', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '' });
      if (measureCurrent) measureEndLineRef.current = series.createPriceLine({ price: measureCurrent.price, color: '#2962ff', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '' });
    }
  }, [isMeasureMode, measureStart, measureCurrent, mainSeriesRef.current]);

  useEffect(() => {
    const chart = chartRef.current; if (!chart) return;
    const handleMove = (p: any) => {
      if (isMeasureModeRef.current && measureStartRef.current && p.point && mainSeriesRef.current) {
        const pr = (mainSeriesRef.current as any).coordinateToPrice(p.point.y);
        if (pr !== null) setMeasureCurrent({ price: pr, time: p.time as number, logical: p.logical ?? null, x: p.point.x, y: p.point.y });
      }
      if (p.point && mainSeriesRef.current && processedTransactionsRef.current.length > 0 && !isMeasureModeRef.current) {
        const ts = chart.timeScale(); let cand: Transaction | null = null; let minD = Infinity;
        const avT = data.map(d => d.time as number);
        for (const t of processedTransactionsRef.current) {
          const rt = avT.length > 0 ? avT.reduce((pr, c) => (Math.abs(c - t.timestamp) < Math.abs(pr - t.timestamp) ? c : pr)) : t.timestamp;
          const x = ts.timeToCoordinate(rt as UTCTimestamp); const y = mainSeriesRef.current.priceToCoordinate(t.price);
          if (x === null || y === null) continue;
          const dist = Math.sqrt((x - p.point.x)**2 + (y - p.point.y)**2);
          if (dist < 20 && dist < minD) { minD = dist; cand = t; }
        }
        if (cand && mainSeriesRef.current) {
            const rt = avT.length > 0 ? avT.reduce((pr, c) => (Math.abs(c - cand!.timestamp) < Math.abs(pr - cand!.timestamp) ? c : pr)) : cand.timestamp;
            const x = ts.timeToCoordinate(rt as UTCTimestamp); const y = mainSeriesRef.current.priceToCoordinate(cand.price);
            if (x !== null && y !== null) setMarkerTooltip({ visible: true, x, y, transaction: cand });
        } else setMarkerTooltip(prev => ({ ...prev, visible: false }));
      } else setMarkerTooltip(prev => ({ ...prev, visible: false }));
    };
    const handleClick = (p: MouseEventParams) => {
      if (!isMeasureModeRef.current || !mainSeriesRef.current || !p.point) return;
      const pr = (mainSeriesRef.current as any).coordinateToPrice(p.point.y); if (pr === null) return;
      if (measureStartRef.current) { setMeasureStart(null); setMeasureCurrent(null); onMeasureModeChange(false); }
      else setMeasureStart({ price: pr, time: p.time as number, logical: p.logical ?? null, x: p.point.x, y: p.point.y });
    };
    chart.subscribeCrosshairMove(handleMove); chart.subscribeClick(handleClick);
    return () => { chart.unsubscribeCrosshairMove(handleMove); chart.unsubscribeClick(handleClick); };
  }, [mainSeriesRef.current, onMeasureModeChange, data]);

  const sidebarButtonClass = `p-2 rounded-lg transition-all backdrop-blur-md shadow-sm border ${theme === 'dark' ? 'bg-[#1a1c22]/80 border-[#363a45] hover:bg-white/10 text-slate-300' : 'bg-white/80 border-slate-200 hover:bg-slate-50 text-slate-600'}`;
  const sidebarButtonActiveClass = `p-2 rounded-lg transition-all shadow-md border bg-indigo-600 border-indigo-500 text-white`;

  return (
    <div className={isFullscreen ? `relative w-full h-full flex flex-col ${theme === 'dark' ? 'bg-[#0f0f0f]' : 'bg-white'}` : `relative w-full h-full flex flex-col border ${theme === 'dark' ? 'bg-[#161616] border-[#262626]' : 'bg-white border-slate-100'} rounded-2xl overflow-hidden shadow-2xl transition-all duration-300`}>
      <div className={`flex flex-col sm:flex-row sm:items-start justify-between p-6 pb-0 flex-shrink-0 relative z-30 space-y-4 sm:space-y-0`}>
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20"><Activity size={24} className="text-emerald-500" /></div>
          <div>
            <div className="flex items-center space-x-2"><h3 className={`text-xl font-black uppercase tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'} flex items-center`}>Terminal Trading</h3><div className="flex items-center px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 space-x-1"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span></span><span className="text-[9px] font-black text-emerald-500 tracking-wider">LIVE</span></div></div>
            <div className="flex items-center space-x-3 mt-1.5 bg-white/5 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10 shadow-sm w-fit"><span className={`text-sm font-black tracking-tight ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}>{symbol}</span><div className="w-[1px] h-3 bg-white/10" /><span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{interval === '1w' ? '1S' : interval}</span></div>
          </div>
        </div>
        <div className="flex flex-col items-end space-y-4">
          <div className="flex items-center space-x-3">
            <div className={`flex p-1 rounded-xl border ${theme === 'dark' ? 'bg-[#0f0f0f] border-[#262626]' : 'bg-slate-50 border-slate-200'}`}>
              {timeframes.map((tf) => (<button key={tf.value} onClick={() => onIntervalChange?.(tf.value)} className={`px-4 py-2 text-[10px] font-black rounded-lg transition-all uppercase tracking-widest ${interval === tf.value ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-slate-500 hover:text-slate-300'}`}>{tf.label}</button>))}
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={onToggleTheme} className={`p-2 rounded-xl transition-all shadow-lg active:scale-95 border ${theme === 'dark' ? 'bg-[#1a1c22]/80 border-[#363a45] text-slate-300 hover:bg-white/10' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button onClick={onOpenTransactions} className={`p-2 rounded-xl transition-all shadow-lg active:scale-95 border ${theme === 'dark' ? 'bg-[#1a1c22]/80 border-[#363a45] text-slate-300 hover:bg-white/10' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}><List size={18} /></button>
              <button onClick={() => onChartTypeChange?.(chartType === 'candlestick' ? 'line' : 'candlestick')} className={`p-2 rounded-xl transition-all shadow-lg active:scale-95 border ${theme === 'dark' ? 'bg-[#1a1c22]/80 border-[#363a45] text-slate-300 hover:bg-white/10' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{chartType === 'candlestick' ? <CandlestickChart size={18} /> : <LineChart size={18} />}</button>
              <button onClick={() => onToggleFullscreen?.(!isFullscreen)} className={`p-2 rounded-xl transition-all shadow-lg active:scale-95 border ${theme === 'dark' ? 'bg-[#1a1c22]/80 border-[#363a45] text-slate-300 hover:bg-white/10' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}</button>
            </div>
          </div>
        </div>
      </div>
      <div className={`flex-1 relative min-h-0 w-full ${isMeasureMode ? 'cursor-crosshair' : 'cursor-default'}`}>
        <div ref={chartContainerRef} className="w-full h-full" />
        
        {(isLoading || data.length === 0) && (
          <div className={`absolute inset-0 z-50 flex flex-col items-center justify-center ${data.length === 0 ? 'bg-black/20' : 'bg-black/10'} backdrop-blur-[4px] animate-in fade-in duration-300`}>
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-500 blur-3xl opacity-30 scale-150 animate-pulse"></div>
                <Loader2 size={40} className="text-indigo-500 animate-spin relative" />
              </div>
              <div className="text-center">
                  <span className={`text-[10px] font-black uppercase tracking-[0.3em] ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'} animate-pulse block`}>
                    {data.length === 0 ? `Sincronizando ${symbol.split(/[-/]/)[0]}...` : 'Actualizando Datos...'}
                  </span>
                  <span className="text-[8px] font-bold text-slate-500 uppercase mt-1 opacity-70">Cargando datos históricos</span>
              </div>
            </div>
          </div>
        )}

        <div className="absolute top-4 left-4 z-40 flex flex-col space-y-2">
            <button onClick={() => onMeasureModeChange(!isMeasureMode)} className={isMeasureMode ? sidebarButtonActiveClass : sidebarButtonClass} title="Medida"><Ruler size={18} /></button>
            <button onClick={onToggleLabels} className={showLabels ? sidebarButtonActiveClass : sidebarButtonClass} title="Etiquetas"><Tag size={18} /></button>
            <button onClick={onToggleBreakEven} className={showBreakEven ? sidebarButtonActiveClass : sidebarButtonClass} title="Promedio"><Target size={18} /></button>
            <button onClick={onToggleDynamicOpacity} className={useDynamicOpacity ? sidebarButtonActiveClass : sidebarButtonClass} title="Opacidad"><Droplet size={18} /></button>
            <button onClick={onToggleUniformBubbleSize} className={useUniformBubbleSize ? sidebarButtonActiveClass : sidebarButtonClass} title="Tamaño"><Circle size={18} /></button>
            <button onClick={onToggleEMA} className={showEMA ? sidebarButtonActiveClass : sidebarButtonClass} title="EMA"><Activity size={18} /></button>
        </div>
        {isMeasureMode && measureStart && measureCurrent && (
            <>
              <div className="absolute pointer-events-none border border-dashed border-[#2962ff] bg-[#2962ff]/10 z-40" style={{ left: Math.min(measureStart.x, measureCurrent.x), top: Math.min(measureStart.y, measureCurrent.y), width: Math.abs(measureCurrent.x - measureStart.x), height: Math.abs(measureCurrent.y - measureStart.y) }} />
              <div className="absolute z-[100] pointer-events-none p-3 rounded-2xl shadow-2xl flex flex-col items-center justify-center text-center space-y-1 bg-[#2962ff] text-white min-w-[190px] -translate-x-1/2 -translate-y-full mb-4" style={{ left: (measureStart.x + measureCurrent.x) / 2, top: Math.min(measureStart.y, measureCurrent.y) - 5 }}>
                  <div className="text-[15px] font-black whitespace-nowrap">{measureCurrent.price - measureStart.price >= 0 ? '+' : ''}{formatPrice(measureCurrent.price - measureStart.price)} ({( ( (measureCurrent.price - measureStart.price) / measureStart.price ) * 100).toFixed(2)}%)</div>
                  <div className="text-[11px] font-bold opacity-80 whitespace-nowrap">{Math.abs((measureCurrent.logical || 0) - (measureStart.logical || 0))} barras • {formatDuration(measureStart.time, measureCurrent.time)}</div>
              </div>
            </>
        )}
        {markerTooltip.visible && markerTooltip.transaction && !isMeasureMode && (
          <div className={`absolute z-[120] pointer-events-none p-4 rounded-2xl border shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-95 duration-150 flex flex-col min-w-[210px] ${theme === 'dark' ? 'bg-[#1a1c22]/90 border-[#363a45] text-white' : 'bg-white/90 border-slate-200 text-slate-900'}`} style={{ left: markerTooltip.x + 20, top: Math.max(20, markerTooltip.y - 120), transform: markerTooltip.x > (chartContainerRef.current?.clientWidth || 0) * 0.7 ? 'translateX(-110%)' : 'none' }}>
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/10"><span className="text-[10px] font-black uppercase opacity-80">{markerTooltip.transaction.status}</span><span className="text-[9px] font-mono opacity-50">{markerTooltip.transaction.date}</span></div>
            <div className="space-y-2.5">
              <div className="flex justify-between items-center"><span className="text-[9px] font-bold text-slate-500">Precio</span><span className="text-[12px] font-mono font-bold">${markerTooltip.transaction.price.toLocaleString()}</span></div>
              
              {currentPrice && currentPrice > 0 && (
                 <>
                    {(() => {
                        const val = (currentPrice - markerTooltip.transaction!.price) * markerTooltip.transaction!.quantity;
                        const perc = ((currentPrice - markerTooltip.transaction!.price) / markerTooltip.transaction!.price) * 100;
                        const isPos = val >= 0;
                        return (
                            <>
                                <div className="flex justify-between items-center"><span className="text-[9px] font-bold text-slate-500">ROI $</span><span className={`text-[12px] font-mono font-black ${isPos ? 'text-[#089981]' : 'text-[#f23645]'}`}>{isPos ? '+' : ''}{val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                                <div className="flex justify-between items-center"><span className="text-[9px] font-bold text-slate-500">ROI %</span><span className={`text-[12px] font-mono font-black ${isPos ? 'text-[#089981]' : 'text-[#f23645]'}`}>{isPos ? '+' : ''}{perc.toFixed(2)}%</span></div>
                            </>
                        );
                    })()}
                 </>
              )}

              <div className="pt-2 mt-1 border-t border-white/10 flex justify-between items-center"><span className="text-[10px] font-black text-indigo-400">Total</span><span className="text-[15px] font-black">${markerTooltip.transaction.total.toLocaleString()}</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingChart;